import React, { useState, useEffect, useRef } from 'react';
import { Code, Server, Users, Award, ChevronDown, Menu, X, GraduationCap, Building2, Zap, Shield, GitBranch, Database, Cloud, Mail, Send, Linkedin, Calendar } from 'lucide-react';
import { InlineWidget } from 'react-calendly';
import ReCAPTCHA from 'react-google-recaptcha';

export default function Portfolio() {
  // Email bypass flag for local development
  const BYPASS_EMAIL_GATE = process.env.REACT_APP_BYPASS_EMAIL === 'true';

  // API endpoint for storing emails (update after deploying Lambda)
  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

  // reCAPTCHA site key (client-side)
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  const recaptchaRef = useRef(null);

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userEmail, setUserEmail] = useState(BYPASS_EMAIL_GATE ? 'dev@localhost' : '');
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [hasAccess, setHasAccess] = useState(BYPASS_EMAIL_GATE);
  const [contactMessage, setContactMessage] = useState('');
  const [contactMethod, setContactMethod] = useState('email'); // 'email' or 'calendly'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Project Hail Mary themed space images (NASA & themed sci-fi imagery)
  const spaceImages = [
    'https://images-assets.nasa.gov/image/NHQ202211160110/NHQ202211160110~large.jpg', // Cosmic clouds
    'https://images-assets.nasa.gov/image/PIA16695/PIA16695~orig.jpg', // Galaxy
    'https://images-assets.nasa.gov/image/PIA12348/PIA12348~orig.jpg', // Nebula
    'https://images-assets.nasa.gov/image/PIA23122/PIA23122~orig.jpg', // Distant planet
    'https://images-assets.nasa.gov/image/PIA17563/PIA17563~orig.jpg', // Star cluster
    'https://images-assets.nasa.gov/image/PIA15415/PIA15415~orig.jpg', // Deep space
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cycle through background images every 8 seconds
  useEffect(() => {
    if (!hasAccess) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % spaceImages.length);
      }, 8000); // Change image every 8 seconds

      return () => clearInterval(interval);
    }
  }, [hasAccess, spaceImages.length]);

  // Preload images for smooth transitions
  useEffect(() => {
    if (!hasAccess) {
      spaceImages.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }
  }, [hasAccess, spaceImages]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!emailInput) {
      setEmailError('Please enter an email address');
      return;
    }
    if (!validateEmail(emailInput)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // CAPTCHA validation
    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      setEmailError('Please complete the CAPTCHA verification');
      return;
    }

    setIsSubmitting(true);
    setEmailError('');

    try {
      // Store email in DynamoDB via Lambda API
      if (API_ENDPOINT) {
        const response = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: emailInput.toLowerCase().trim(),
            captchaToken: captchaToken || undefined
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();

          // If CAPTCHA verification failed, show error and reset
          if (response.status === 400 && errorData.error?.includes('CAPTCHA')) {
            setEmailError('CAPTCHA verification failed. Please try again.');
            if (recaptchaRef.current) {
              recaptchaRef.current.reset();
            }
            setCaptchaToken(null);
            setIsSubmitting(false);
            return;
          }

          console.warn('Failed to store email:', errorData);
          // Continue anyway - don't block access if API fails
        }
      }

      // Grant access to portfolio
      setUserEmail(emailInput);
      setHasAccess(true);
      setEmailError('');
    } catch (error) {
      console.error('Error storing email:', error);
      // Still grant access even if storage fails - better UX
      setUserEmail(emailInput);
      setHasAccess(true);
      setEmailError('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setEmailError(''); // Clear any CAPTCHA errors
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    setEmailError('CAPTCHA expired. Please verify again.');
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent('Portfolio Inquiry from ' + userEmail);
    const body = encodeURIComponent(
      `From: ${userEmail}\n\nMessage:\n${contactMessage}\n\n---\nSent via Andres Macias Portfolio`
    );
    window.location.href = `mailto:joseandresmacias@icloud.com?subject=${subject}&body=${body}`;
  };

  // Email Gate Screen
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-space-charcoal text-white flex items-center justify-center px-6 relative overflow-hidden">
        {/* Cycling NASA Space Backgrounds */}
        <div className="absolute inset-0" aria-hidden="true">
          {spaceImages.map((imageSrc, index) => (
            <div
              key={imageSrc}
              className="absolute inset-0 transition-opacity duration-2000 ease-in-out"
              style={{
                opacity: index === currentImageIndex ? 0.35 : 0,
                transitionDuration: '2000ms'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-space-charcoal via-transparent to-space-charcoal z-10"></div>
              <img
                src={imageSrc}
                alt=""
                role="presentation"
                className="w-full h-full object-cover"
                loading={index === 0 ? "eager" : "lazy"}
              />
            </div>
          ))}
        </div>

        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-20" aria-hidden="true">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #61FFD0 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-neon-cyan/30 to-neon-cyan/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-neon-cyan/50 shadow-[0_0_30px_rgba(97,255,208,0.3)]" aria-hidden="true">
              <Mail size={40} className="text-neon-cyan" />
            </div>
            <h1 className="text-4xl md:text-5xl font-antonio font-bold mb-4 uppercase tracking-wider">Welcome</h1>
            <p className="text-white/70 text-lg font-inter">Enter your email to view my portfolio</p>
          </div>

          <form onSubmit={handleEmailSubmit} className="bg-[#1a1a1a]/80 backdrop-blur-sm p-8 rounded-2xl border border-neon-cyan/30 shadow-[0_0_40px_rgba(97,255,208,0.15)]" aria-label="Email gate form">
            <div className="mb-6">
              <label htmlFor="email-gate-input" className="block text-sm font-inter font-medium mb-3 text-white/80 uppercase tracking-wider">Email Address</label>
              <input
                id="email-gate-input"
                type="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setEmailError('');
                }}
                placeholder="your.email@example.com"
                aria-invalid={emailError ? 'true' : 'false'}
                aria-describedby={emailError ? 'email-error' : undefined}
                className="w-full px-4 py-3 bg-space-charcoal/50 border border-neon-cyan/30 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-neon-cyan focus:ring-2 focus:ring-neon-cyan/50 focus:shadow-[0_0_15px_rgba(97,255,208,0.3)] transition-all font-inter"
              />
              {emailError && (
                <p id="email-error" className="mt-2 text-neon-red text-sm font-inter" role="alert">
                  {emailError}
                </p>
              )}
            </div>

            {/* reCAPTCHA */}
            {RECAPTCHA_SITE_KEY && (
              <div className="mb-6 flex justify-center">
                <div className="transform scale-90 sm:scale-100 origin-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    theme="dark"
                    aria-label="reCAPTCHA verification"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || (RECAPTCHA_SITE_KEY && !captchaToken)}
              className="w-full py-3 bg-neon-cyan text-space-charcoal font-inter font-bold rounded-xl hover:bg-neon-cyan/90 hover:shadow-[0_0_25px_rgba(97,255,208,0.6)] transition-all duration-300 uppercase tracking-wider transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-space-charcoal disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isSubmitting ? 'Processing...' : 'Continue'}
            </button>

            <p className="mt-4 text-center text-white/60 text-xs font-inter">
              Your email is only used for this session
            </p>
          </form>
        </div>

        {/* Image Progress Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2" aria-label="Background image carousel progress">
          {spaceImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`h-2 rounded-full transition-all duration-500 ${
                index === currentImageIndex
                  ? 'w-8 bg-neon-cyan shadow-[0_0_10px_rgba(97,255,208,0.6)]'
                  : 'w-2 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`View image ${index + 1} of ${spaceImages.length}`}
              aria-current={index === currentImageIndex}
            />
          ))}
        </div>
      </div>
    );
  }

  // Main Portfolio UI
  return (
    <div className="min-h-screen bg-space-charcoal text-white font-inter">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-6 focus:py-3 focus:bg-neon-cyan focus:text-space-charcoal focus:rounded-xl focus:font-bold focus:shadow-[0_0_25px_rgba(97,255,208,0.6)]"
      >
        Skip to main content
      </a>

      {/* Navigation */}
      <nav aria-label="Main navigation" className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-space-charcoal/90 backdrop-blur-sm shadow-[0_4px_20px_rgba(97,255,208,0.1)] border-b border-neon-cyan/20' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
          <div className="text-white font-antonio font-bold text-2xl tracking-wider uppercase" role="banner">Andres Macias</div>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-neon-cyan lg:hidden"
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
          </button>
          <div className="hidden lg:flex gap-10" role="menubar">
            {['overview', 'expertise', 'experience', 'education', 'contact'].map(item => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className="text-white/80 hover:text-neon-cyan transition-all text-sm uppercase tracking-[0.15em] font-semibold focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-space-charcoal rounded px-2 py-1"
                role="menuitem"
                aria-label={`Navigate to ${item} section`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden bg-space-charcoal/95 backdrop-blur-sm px-6 py-6 border-t border-neon-cyan/20" role="menu">
            {['overview', 'expertise', 'experience', 'education', 'contact'].map(item => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className="block w-full text-left text-white/80 hover:text-neon-cyan py-3 uppercase text-sm tracking-wider transition-all focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-space-charcoal rounded"
                role="menuitem"
                aria-label={`Navigate to ${item} section`}
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="main-content" className="relative h-screen flex items-center justify-center overflow-hidden" aria-labelledby="hero-heading">
        {/* NASA Space Background */}
        <div className="absolute inset-0 opacity-40" aria-hidden="true">
          <div className="absolute inset-0 bg-gradient-to-b from-space-charcoal via-transparent to-space-charcoal z-10"></div>
          <img
            src="https://images-assets.nasa.gov/image/PIA16695/PIA16695~orig.jpg"
            alt=""
            role="presentation"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-space-charcoal/50 via-transparent to-space-charcoal/50" aria-hidden="true"></div>
        <div className="absolute inset-0 opacity-15" aria-hidden="true">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #61FFD0 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="relative text-center z-10 px-6 max-w-5xl">
          <p className="text-sm uppercase tracking-[0.3em] text-neon-cyan mb-6 font-semibold">Platform & Product Leader</p>
          <h1 id="hero-heading" className="text-6xl md:text-8xl lg:text-9xl font-antonio font-bold mb-8 tracking-tight uppercase">
            <span className="bg-gradient-to-r from-white via-neon-cyan/80 to-white bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(97,255,208,0.3)]">
              Andres Macias
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-12 leading-relaxed">
            12+ years designing, securing, and scaling enterprise cloud infrastructure and B2B SaaS products in regulated industries
          </p>
          <button
            onClick={() => scrollToSection('overview')}
            className="text-neon-cyan/80 hover:text-neon-cyan transition-all flex flex-col items-center mx-auto group focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-space-charcoal rounded px-4 py-2"
            aria-label="Scroll to overview section"
          >
            <span className="text-xs uppercase tracking-widest mb-2 font-semibold">Explore</span>
            <ChevronDown className="animate-bounce group-hover:drop-shadow-[0_0_10px_rgba(97,255,208,0.5)]" size={24} aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* Overview Section */}
      <section id="overview" className="py-24 px-6 bg-[#1a1a1a]" aria-labelledby="overview-heading">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-neon-cyan/10 rounded-full text-sm uppercase tracking-[0.2em] text-neon-cyan mb-6 border border-neon-cyan/30" role="presentation">
              Professional Summary
            </div>
            <h2 id="overview-heading" className="text-4xl md:text-5xl font-antonio font-bold mb-6 uppercase tracking-wider">Platform & Product Leader</h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
              Designing, securing, and scaling enterprise cloud infrastructure and B2B SaaS products in regulated industries
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-[#262626] p-8 rounded-2xl border border-neon-cyan/20 hover:border-neon-cyan/40 transition">
              <div className="text-5xl font-antonio font-bold text-neon-cyan mb-2" aria-label="12 plus years">12+</div>
              <div className="text-white/70 text-sm uppercase tracking-wider">Years of Experience</div>
            </div>
            <div className="bg-[#262626] p-8 rounded-2xl border border-neon-cyan/20 hover:border-neon-cyan/40 transition">
              <div className="text-5xl font-antonio font-bold text-neon-cyan mb-2" aria-label="5 million plus">5M+</div>
              <div className="text-white/70 text-sm uppercase tracking-wider">Daily Transactions</div>
            </div>
            <div className="bg-[#262626] p-8 rounded-2xl border border-neon-cyan/20 hover:border-neon-cyan/40 transition">
              <div className="text-5xl font-antonio font-bold text-neon-cyan mb-2" aria-label="99.95 percent">99.95%</div>
              <div className="text-white/70 text-sm uppercase tracking-wider">Platform Uptime</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#262626] to-[#1a1a1a] p-10 rounded-2xl border border-neon-cyan/30 shadow-[0_0_40px_rgba(97,255,208,0.1)]">
            <h3 className="text-2xl font-antonio font-bold mb-6 uppercase tracking-wide text-neon-cyan">Core Focus</h3>
            <p className="text-lg text-white/80 leading-relaxed mb-6">
              Deep expertise in cloud-native architecture, containerized platforms, and CI/CD automation across AWS, GCP, and Azure with hands-on delivery of SOC 2, PCI-DSS, and HIPAA-compliant systems.
            </p>
            <p className="text-lg text-white/80 leading-relaxed">
              Operating at the intersection of security, platform engineering, and product strategy — translating regulatory requirements and customer needs into scalable architectures and actionable roadmaps. Known for building high-performing distributed teams, accelerating developer velocity without compromising reliability, and earning trust across engineering, security, and executive stakeholders.
            </p>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className="py-24 px-6 bg-[#262626]" aria-labelledby="expertise-heading">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="inline-block px-4 py-2 bg-neon-cyan/10 rounded-full text-sm uppercase tracking-[0.2em] text-neon-cyan mb-6 border border-neon-cyan/30" role="presentation">
              Core Competencies
            </div>
            <h2 id="expertise-heading" className="text-4xl md:text-5xl font-antonio font-bold mb-4 uppercase tracking-wider">Technical Expertise</h2>
            <p className="text-xl text-white/70 max-w-2xl">Comprehensive capabilities across cloud platforms, security, and leadership</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Cloud,
                title: 'Cloud Platforms & Architecture',
                desc: 'AWS (EC2, ECS, EKS, Lambda, SNS, SQS, RDS, S3), GCP, Azure. Cloud-native architecture, microservices, multi-region DR.',
                color: 'from-neon-cyan/20 to-neon-cyan/10'
              },
              {
                icon: Shield,
                title: 'Security & Compliance',
                desc: 'Zero Trust, OAuth 2.0, OIDC, SAML, SSO, OWASP, DAST, Prisma Cloud, SOC 2, PCI-DSS, HIPAA compliance frameworks.',
                color: 'from-neon-red/20 to-neon-red/10'
              },
              {
                icon: GitBranch,
                title: 'DevOps & CI/CD',
                desc: 'GitHub Actions, Jenkins, Argo CD, Terraform IaC, Docker, Kubernetes, zero-downtime deployments, observability.',
                color: 'from-neon-cyan/20 to-neon-cyan/10'
              },
              {
                icon: Code,
                title: 'Development & Frameworks',
                desc: 'Java Spring Boot, Python, JavaScript/TypeScript, Node.js, React, REST API, GraphQL, Gradle, Maven.',
                color: 'from-neon-cyan/20 to-neon-cyan/10'
              },
              {
                icon: Users,
                title: 'Leadership & Product',
                desc: 'Technical product ownership, roadmap definition, distributed team leadership, stakeholder management, DEI, Agile/SAFe.',
                color: 'from-neon-red/20 to-neon-red/10'
              },
              {
                icon: Zap,
                title: 'AI & Developer Productivity',
                desc: 'GitHub Copilot adoption, AI-assisted development, prompt engineering, developer experience optimization.',
                color: 'from-neon-cyan/20 to-neon-cyan/10'
              }
            ].map((item, idx) => (
              <article key={idx} className={`bg-gradient-to-br ${item.color} backdrop-blur-sm p-8 rounded-2xl border border-neon-cyan/30 hover:border-neon-cyan/50 hover:shadow-[0_0_30px_rgba(97,255,208,0.2)] transition-all group`}>
                <item.icon className="text-neon-cyan mb-6 group-hover:scale-110 transition" size={48} aria-hidden="true" />
                <h3 className="text-xl font-antonio font-bold mb-3 uppercase tracking-wide text-white">{item.title}</h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-24 px-6 bg-[#1a1a1a]" aria-labelledby="experience-heading">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="inline-block px-4 py-2 bg-neon-cyan/10 rounded-full text-sm uppercase tracking-[0.2em] text-neon-cyan mb-6 border border-neon-cyan/30" role="presentation">
              Professional Experience
            </div>
            <h2 id="experience-heading" className="text-4xl md:text-5xl font-antonio font-bold mb-4 uppercase tracking-wider">Proven track record of delivery</h2>
            <p className="text-xl text-white/70 max-w-2xl">Fortune 500 experience with cutting-edge technology implementation</p>
          </div>

          <div className="space-y-8">
            {/* Guardian Life */}
            <article className="bg-gradient-to-br from-[#262626] to-[#1a1a1a] rounded-2xl border border-neon-cyan/30 overflow-hidden hover:border-neon-cyan/50 hover:shadow-[0_0_40px_rgba(97,255,208,0.15)] transition-all group">
              <div className="p-8 md:p-10">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-neon-cyan/30 to-neon-cyan/10 rounded-xl flex items-center justify-center border border-neon-cyan/50 flex-shrink-0" aria-hidden="true">
                      <Building2 size={32} className="text-neon-cyan" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-antonio font-bold mb-2 uppercase tracking-wide">Senior Product Owner – Security & Platform Solutions</h3>
                      <p className="text-white/70 font-semibold">Guardian Life Insurance Company of America</p>
                      <p className="text-white/50 text-sm">Holmdel, NJ (Remote)</p>
                    </div>
                  </div>
                  <time className="text-white/60 text-sm font-semibold whitespace-nowrap">Nov 2019 – Jul 2025</time>
                </div>

                <p className="text-white/80 mb-6 leading-relaxed">
                  Led product strategy and implementation for Guardian Connect API platform processing 5M+ daily transactions at 99.95% uptime. Served as primary security solutions architect for cross-functional teams across the US, India, Europe, and Latin America.
                </p>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-neon-cyan/10 p-4 rounded-xl border border-neon-cyan/30">
                    <Shield size={20} className="text-neon-cyan mb-2" aria-hidden="true" />
                    <div className="text-sm text-white/80 font-semibold">60% reduction in security incidents</div>
                  </div>
                  <div className="bg-neon-cyan/10 p-4 rounded-xl border border-neon-cyan/30">
                    <Cloud size={20} className="text-neon-cyan mb-2" aria-hidden="true" />
                    <div className="text-sm text-white/80 font-semibold">99.95% platform uptime</div>
                  </div>
                  <div className="bg-neon-cyan/10 p-4 rounded-xl border border-neon-cyan/30">
                    <Zap size={20} className="text-neon-cyan mb-2" aria-hidden="true" />
                    <div className="text-sm text-white/80 font-semibold">20-25% productivity boost</div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-antonio font-bold uppercase tracking-wide text-neon-cyan mb-3">Security & Compliance</h4>
                    <ul className="space-y-2 text-white/70 leading-relaxed">
                      <li>• Architected Prisma Cloud security operations platform integrating OWASP DAST scanning and CI/CD policy gates, reducing production security incidents by 60%</li>
                      <li>• Designed OAuth 2.0 and OIDC federation with JWKS rotation for HIPAA-compliant multi-tenant APIs securing millions of daily transactions</li>
                      <li>• Built risk assessment frameworks and policy optimization across 1,000+ microservices, accelerating SOC 2 Type II certification cycles</li>
                      <li>• Partnered with security teams to migrate legacy firewall policies to NGFW and Prisma Cloud configurations with zero security regressions</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-antonio font-bold uppercase tracking-wide text-neon-cyan mb-3">Cloud Architecture & Operations</h4>
                    <ul className="space-y-2 text-white/70 leading-relaxed">
                      <li>• Designed event-driven, containerized cloud architecture using AWS ECS, EKS, Lambda, and Terraform IaC across 100+ microservices</li>
                      <li>• Improved cloud infrastructure cost efficiency by 30% while sustaining 99.95% uptime through optimized orchestration and capacity planning</li>
                      <li>• Architected multi-region disaster recovery strategy and implemented AWS VPC, IAM, and security group standards</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-antonio font-bold uppercase tracking-wide text-neon-cyan mb-3">DevOps & Automation</h4>
                    <ul className="space-y-2 text-white/70 leading-relaxed">
                      <li>• Built enterprise CI/CD pipelines using GitHub Actions and Jenkins, enabling daily zero-downtime deployments with integrated security scanning</li>
                      <li>• Pioneered GitHub Copilot adoption for 200+ developers, achieving 20-25% measurable improvement in developer productivity</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-antonio font-bold uppercase tracking-wide text-neon-cyan mb-3">Leadership & Stakeholder Management</h4>
                    <ul className="space-y-2 text-white/70 leading-relaxed">
                      <li>• Led, mentored, and grew a team of 15+ senior engineers across platform engineering, security, and DevOps disciplines</li>
                      <li>• Assessed enterprise customer security requirements and translated them into prioritized product roadmaps</li>
                      <li>• Standardized Agile planning rituals and SAFe ceremonies across distributed teams, improving on-time delivery rate by 40%</li>
                    </ul>
                  </div>
                </div>
              </div>
            </article>

            {/* AKC */}
            <article className="bg-gradient-to-br from-[#262626] to-[#1a1a1a] rounded-2xl border border-neon-cyan/30 overflow-hidden hover:border-neon-cyan/50 hover:shadow-[0_0_40px_rgba(97,255,208,0.15)] transition-all group">
              <div className="p-8 md:p-10">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-neon-cyan/30 to-neon-cyan/10 rounded-xl flex items-center justify-center border border-neon-cyan/50 flex-shrink-0" aria-hidden="true">
                      <Code size={32} className="text-neon-cyan" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-antonio font-bold mb-2 uppercase tracking-wide">Senior Lead Software Engineer – Platform & Security</h3>
                      <p className="text-white/70 font-semibold">American Kennel Club</p>
                      <p className="text-white/50 text-sm">Raleigh, NC</p>
                    </div>
                  </div>
                  <time className="text-white/60 text-sm font-semibold whitespace-nowrap">May 2013 - Nov 2019</time>
                </div>

                <p className="text-white/80 mb-4 leading-relaxed">
                  Drove platform modernization for national consumer organization with millions of active members. Transformed legacy systems into cloud-native, secure architecture.
                </p>

                <ul className="space-y-2 text-white/70 leading-relaxed">
                  <li>• Designed AWS cloud architecture including VPC, security groups, IAM policies, and multi-region DR; implemented Docker and Kubernetes containerization reducing operational overhead by 30%+</li>
                  <li>• Established secure SDLC processes with automated vulnerability scanning and security gates in CI/CD pipelines; reduced security vulnerabilities by 55%, achieved PCI-DSS compliance, and reduced MTTR by 65%</li>
                  <li>• Built Jenkins CI/CD pipelines enabling transition from monthly releases to daily continuous delivery with zero-downtime deployments</li>
                  <li>• Directed distributed engineering teams across the US and Costa Rica; communicated technical strategy to C-suite executives</li>
                </ul>
              </div>
            </article>

            {/* Research Square */}
            <article className="bg-gradient-to-br from-[#262626] to-[#1a1a1a] rounded-2xl border border-neon-cyan/30 overflow-hidden hover:border-neon-cyan/50 hover:shadow-[0_0_40px_rgba(97,255,208,0.15)] transition-all group">
              <div className="p-8 md:p-10">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-neon-cyan/30 to-neon-cyan/10 rounded-xl flex items-center justify-center border border-neon-cyan/50 flex-shrink-0" aria-hidden="true">
                      <Server size={32} className="text-neon-cyan" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-antonio font-bold mb-2 uppercase tracking-wide">Senior Software Engineer</h3>
                      <p className="text-white/70 font-semibold">Research Square</p>
                      <p className="text-white/50 text-sm">Durham, NC</p>
                    </div>
                  </div>
                  <time className="text-white/60 text-sm font-semibold whitespace-nowrap">Jun 2015 – Mar 2016</time>
                </div>

                <ul className="space-y-2 text-white/70 leading-relaxed">
                  <li>• Built global e-commerce and content delivery infrastructure for academic publishing platform serving 50+ countries</li>
                  <li>• Designed secure payment processing integrations and scalable REST API services</li>
                </ul>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Education & Contact Section */}
      <section id="education" className="py-24 px-6 bg-[#262626]" aria-labelledby="education-heading">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="inline-block px-4 py-2 bg-neon-cyan/10 rounded-full text-sm uppercase tracking-[0.2em] text-neon-cyan mb-6 border border-neon-cyan/30" role="presentation">
              Education & Certifications
            </div>
            <h2 id="education-heading" className="text-4xl md:text-5xl font-antonio font-bold mb-4 uppercase tracking-wider">Continuous learning & growth</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <article className="bg-gradient-to-br from-[#1a1a1a] to-[#262626] p-10 rounded-2xl border border-neon-cyan/30 hover:border-neon-cyan/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-neon-cyan/30 to-neon-cyan/10 rounded-xl flex items-center justify-center mb-6 border border-neon-cyan/50" aria-hidden="true">
                <GraduationCap size={32} className="text-neon-cyan" />
              </div>
              <h3 className="text-2xl font-antonio font-bold mb-4 uppercase tracking-wide text-neon-cyan">Education</h3>
              <p className="text-xl text-white/90 font-semibold mb-2">Bachelor of Engineering</p>
              <p className="text-white/70 mb-1">Computer and Information Systems Security / Information Assurance</p>
              <p className="text-white/60 mb-2">Universidad de Guayaquil, Ecuador</p>
            </article>

            <article className="bg-gradient-to-br from-[#1a1a1a] to-[#262626] p-10 rounded-2xl border border-neon-cyan/30 hover:border-neon-cyan/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-neon-cyan/30 to-neon-cyan/10 rounded-xl flex items-center justify-center mb-6 border border-neon-cyan/50" aria-hidden="true">
                <Award size={32} className="text-neon-cyan" />
              </div>
              <h3 className="text-2xl font-antonio font-bold mb-6 uppercase tracking-wide text-neon-cyan">Certifications</h3>
              <ul className="space-y-4" role="list">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-neon-cyan rounded-full flex-shrink-0" aria-hidden="true"></div>
                  <div>
                    <p className="font-semibold text-white">IASA CITA-Foundation Architect</p>
                    <p className="text-white/60 text-sm">IASA Global — February 2025</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-neon-cyan rounded-full flex-shrink-0" aria-hidden="true"></div>
                  <div>
                    <p className="font-semibold text-white">Six Sigma Yellow Belt</p>
                    <p className="text-white/60 text-sm">April 2023</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-neon-cyan rounded-full flex-shrink-0" aria-hidden="true"></div>
                  <div>
                    <p className="font-semibold text-white">ITIL Certified Engineer</p>
                    <p className="text-white/60 text-sm">AXELOS — November 2016</p>
                  </div>
                </li>
              </ul>
              <div className="flex gap-3 mt-6" role="group" aria-label="Professional profile links">
                <a
                  href="https://www.credly.com/users/andres-macias.43cbbde6/badges#credly"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-6 py-3 bg-neon-cyan/10 hover:bg-neon-cyan/20 rounded-xl transition-all text-sm font-semibold text-center border border-neon-cyan/30 hover:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-[#262626]"
                  aria-label="View certifications on Credly"
                >
                  View Credly Profile →
                </a>
                <a
                  href="http://linkedin.com/in/andresmacias/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-neon-cyan/10 hover:bg-neon-cyan/20 rounded-xl transition-all flex items-center justify-center gap-2 text-sm font-semibold border border-neon-cyan/30 hover:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-[#262626]"
                  aria-label="Visit LinkedIn profile"
                >
                  <Linkedin size={20} aria-hidden="true" />
                  LinkedIn
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-space-charcoal relative overflow-hidden" aria-labelledby="contact-heading">
        {/* NASA Space Background */}
        <div className="absolute inset-0 opacity-20" aria-hidden="true">
          <div className="absolute inset-0 bg-gradient-to-b from-space-charcoal via-transparent to-space-charcoal z-10"></div>
          <img
            src="https://images-assets.nasa.gov/image/PIA12348/PIA12348~orig.jpg"
            alt=""
            role="presentation"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-4xl mx-auto relative z-20">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-neon-cyan/10 rounded-full text-sm uppercase tracking-[0.2em] text-neon-cyan mb-6 border border-neon-cyan/30" role="presentation">
              Get in Touch
            </div>
            <h2 id="contact-heading" className="text-4xl md:text-5xl font-antonio font-bold mb-4 uppercase tracking-wider text-white">Let's work together</h2>
            <p className="text-xl text-white/70 font-inter">Have a project or opportunity? I'd love to hear from you</p>
          </div>

          {/* Contact Method Toggle */}
          <div className="flex gap-4 mb-8 justify-center" role="group" aria-label="Contact method selection">
            <button
              onClick={() => setContactMethod('email')}
              className={`px-6 py-3 rounded-xl font-inter font-semibold uppercase text-sm tracking-wider transition-all duration-300 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-space-charcoal ${
                contactMethod === 'email'
                  ? 'bg-neon-cyan text-space-charcoal shadow-[0_0_20px_rgba(97,255,208,0.5)]'
                  : 'bg-white/10 text-white/60 hover:bg-white/20 border border-white/20'
              }`}
              aria-pressed={contactMethod === 'email'}
              aria-label="Select email contact method"
            >
              <Mail size={18} aria-hidden="true" />
              Send Message
            </button>
            <button
              onClick={() => setContactMethod('calendly')}
              className={`px-6 py-3 rounded-xl font-inter font-semibold uppercase text-sm tracking-wider transition-all duration-300 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-space-charcoal ${
                contactMethod === 'calendly'
                  ? 'bg-neon-cyan text-space-charcoal shadow-[0_0_20px_rgba(97,255,208,0.5)]'
                  : 'bg-white/10 text-white/60 hover:bg-white/20 border border-white/20'
              }`}
              aria-pressed={contactMethod === 'calendly'}
              aria-label="Select calendar scheduling method"
            >
              <Calendar size={18} aria-hidden="true" />
              Schedule Meeting
            </button>
          </div>

          {/* Contact Form or Calendly */}
          <div className="bg-gradient-to-br from-[#1a1a1a]/90 to-space-charcoal/90 backdrop-blur-sm p-10 rounded-2xl border border-neon-cyan/30 shadow-[0_0_40px_rgba(97,255,208,0.15)]">
            {contactMethod === 'email' ? (
              <form onSubmit={handleContactSubmit} aria-label="Contact form">
                <div className="mb-6">
                  <label htmlFor="contact-email" className="block text-sm font-inter font-medium mb-3 text-white/80 uppercase tracking-wider">Your Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    value={userEmail}
                    readOnly
                    aria-readonly="true"
                    className="w-full px-4 py-3 bg-space-charcoal/50 border border-white/10 rounded-xl text-white/60 cursor-not-allowed font-inter"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="contact-message" className="block text-sm font-inter font-medium mb-3 text-white/80 uppercase tracking-wider">Message</label>
                  <textarea
                    id="contact-message"
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    placeholder="Tell me about your project or opportunity..."
                    rows={6}
                    required
                    aria-required="true"
                    className="w-full px-4 py-3 bg-space-charcoal/50 border border-neon-cyan/30 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-neon-cyan focus:ring-2 focus:ring-neon-cyan/50 focus:shadow-[0_0_15px_rgba(97,255,208,0.3)] transition-all resize-none font-inter"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-neon-cyan text-space-charcoal font-inter font-bold rounded-xl hover:bg-neon-cyan/90 hover:shadow-[0_0_25px_rgba(97,255,208,0.6)] transition-all duration-300 flex items-center justify-center gap-3 uppercase tracking-wider transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-space-charcoal"
                  aria-label="Send contact message via email"
                >
                  <Send size={20} aria-hidden="true" />
                  Send Message
                </button>

                <p className="mt-4 text-center text-white/60 text-sm font-inter" role="status" aria-live="polite">
                  This will open your default email client with the message pre-filled
                </p>
              </form>
            ) : (
              <div className="min-h-[600px]" role="region" aria-label="Calendly scheduling widget">
                <InlineWidget
                  url="https://calendly.com/andresmacias/new-meeting"
                  styles={{
                    height: '600px',
                    minWidth: '100%',
                  }}
                  pageSettings={{
                    backgroundColor: 'transparent',
                    hideEventTypeDetails: false,
                    hideLandingPageDetails: false,
                    primaryColor: '61FFD0',
                    textColor: 'ffffff'
                  }}
                />
                <p className="mt-4 text-center text-white/60 text-sm font-inter">
                  Select a time that works best for you
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] border-t border-neon-cyan/20 py-12 px-6" role="contentinfo">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/60 text-sm tracking-wider">
            © 2026 Andres Macias. Platform & Product Leader. Built for scale, engineered for impact.
          </p>
        </div>
      </footer>
    </div>
  );
}
